import type { NodeChange } from '@xyflow/react';

import type { Node } from '../types';

const MAX_HISTORY_ENTRIES = 20;
const NESTED_NODE_FIELDS = new Set(['data', 'measured', 'position', 'style']);

type NodeRecord = Record<string, unknown>;

export interface DiagramNodeHistoryAction {
  type:
    | 'NODE_DRAG_START'
    | 'NODE_DRAG_STOP'
    | 'NODE_RESIZE_START'
    | 'NODE_RESIZE_END'
    | 'UNDO_HISTORY'
    | 'REDO_HISTORY';
}

export interface DiagramNodeHistoryEntry {
  actionType: string;
  /** Node values to restore when this entry is applied. */
  nodes: Node[];
  /** Original indexes for nodes that the action removed. */
  nodeIndexes: Record<string, number>;
  /** Node values after the action. These identify the fields to restore. */
  sourceNodes: Node[];
  /** Caller-owned values that must change atomically with the nodes. */
  supplementalSnapshot?: unknown;
}

export interface DiagramNodeHistoryGesture {
  kind: 'drag' | 'resize';
  nodes: Node[];
}

export interface DiagramNodeHistory {
  past: DiagramNodeHistoryEntry[];
  future: DiagramNodeHistoryEntry[];
  gesture: DiagramNodeHistoryGesture | null;
}

export interface DiagramNodeHistoryState {
  nodes: Node[];
  nodeHistory: DiagramNodeHistory;
}

export interface NodeHistorySupplement<State extends DiagramNodeHistoryState> {
  createSnapshot: (snapshot: State, source: State) => unknown | undefined;
  restoreSnapshot: (state: State, snapshot: unknown) => State;
}

export function createEmptyNodeHistory(): DiagramNodeHistory {
  return {
    past: [],
    future: [],
    gesture: null,
  };
}

function createNodeSnapshot(node: Node): Node {
  return {
    ...node,
    position: { ...node.position },
    data: { ...node.data },
    ...(node.measured ? { measured: { ...node.measured } } : {}),
    ...(node.style ? { style: { ...node.style } } : {}),
  } as Node;
}

function createNodeSnapshots(nodes: Node[]): Node[] {
  return nodes.map(createNodeSnapshot);
}

function asNodeRecord(value: unknown): NodeRecord {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return {};
  }
  return value as NodeRecord;
}

function getChangedKeys(snapshot: NodeRecord, source: NodeRecord): string[] {
  return Array.from(
    new Set([...Object.keys(snapshot), ...Object.keys(source)])
  ).filter((key) => !Object.is(snapshot[key], source[key]));
}

function restoreChangedFields(
  snapshot: NodeRecord,
  source: NodeRecord,
  current: NodeRecord,
  changedKeys = getChangedKeys(snapshot, source)
): NodeRecord {
  const restored = { ...current };

  for (const key of changedKeys) {
    if (Object.prototype.hasOwnProperty.call(snapshot, key)) {
      restored[key] = snapshot[key];
    } else {
      delete restored[key];
    }
  }

  return restored;
}

function restoreNodeFields(snapshot: Node, source: Node, current: Node): Node {
  const snapshotRecord = snapshot as unknown as NodeRecord;
  const sourceRecord = source as unknown as NodeRecord;
  const currentRecord = current as unknown as NodeRecord;
  // Restore only fields that the recorded action changed. Keep later changes,
  // such as text-note content and auto-sized dimensions, on the current node.
  const changedKeys = getChangedKeys(snapshotRecord, sourceRecord);
  const topLevelChangedKeys = changedKeys.filter(
    (key) => !NESTED_NODE_FIELDS.has(key)
  );
  const restored = restoreChangedFields(
    snapshotRecord,
    sourceRecord,
    currentRecord,
    topLevelChangedKeys
  );

  for (const key of changedKeys) {
    if (!NESTED_NODE_FIELDS.has(key)) continue;

    const snapshotFields = asNodeRecord(snapshotRecord[key]);
    const sourceFields = asNodeRecord(sourceRecord[key]);
    const nestedChangedKeys = getChangedKeys(snapshotFields, sourceFields);
    if (nestedChangedKeys.length === 0) continue;

    restored[key] = restoreChangedFields(
      snapshotFields,
      sourceFields,
      asNodeRecord(currentRecord[key]),
      nestedChangedKeys
    );
  }

  return restored as unknown as Node;
}

function didNodeChange(snapshot: Node, source: Node): boolean {
  const snapshotRecord = snapshot as unknown as NodeRecord;
  const sourceRecord = source as unknown as NodeRecord;

  return getChangedKeys(snapshotRecord, sourceRecord).some((key) => {
    if (!NESTED_NODE_FIELDS.has(key)) return true;
    return (
      getChangedKeys(
        asNodeRecord(snapshotRecord[key]),
        asNodeRecord(sourceRecord[key])
      ).length > 0
    );
  });
}

function createHistoryEntry(
  actionType: string,
  nodes: Node[],
  sourceNodes: Node[]
): DiagramNodeHistoryEntry {
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const sourceNodesById = new Map(sourceNodes.map((node) => [node.id, node]));
  const changedNodeIds = new Set<string>();

  nodes.forEach((node) => {
    const sourceNode = sourceNodesById.get(node.id);
    if (!sourceNode || didNodeChange(node, sourceNode)) {
      changedNodeIds.add(node.id);
    }
  });
  sourceNodes.forEach((node) => {
    if (!nodesById.has(node.id)) changedNodeIds.add(node.id);
  });

  return {
    actionType,
    nodes: createNodeSnapshots(
      nodes.filter((node) => changedNodeIds.has(node.id))
    ),
    nodeIndexes: Object.fromEntries(
      nodes.flatMap((node, index) =>
        changedNodeIds.has(node.id) ? [[node.id, index]] : []
      )
    ),
    sourceNodes: createNodeSnapshots(
      sourceNodes.filter((node) => changedNodeIds.has(node.id))
    ),
  };
}

function restoreNodeSnapshot(
  snapshot: Node[],
  snapshotIndexes: Record<string, number>,
  sourceSnapshot: Node[],
  currentNodes: Node[]
): Node[] {
  const snapshotById = new Map(snapshot.map((node) => [node.id, node]));
  const sourceById = new Map(sourceSnapshot.map((node) => [node.id, node]));

  const restoredNodes = currentNodes.flatMap((node) => {
    const sourceNode = sourceById.get(node.id);
    if (!sourceNode) return [node];

    const snapshotNode = snapshotById.get(node.id);
    if (!snapshotNode) return [];

    return [restoreNodeFields(snapshotNode, sourceNode, node)];
  });

  const restoredNodeIds = new Set(restoredNodes.map((node) => node.id));
  snapshot.forEach((node, index) => {
    if (sourceById.has(node.id) || restoredNodeIds.has(node.id)) return;

    restoredNodes.splice(
      Math.min(snapshotIndexes[node.id] ?? index, restoredNodes.length),
      0,
      createNodeSnapshot(node)
    );
    restoredNodeIds.add(node.id);
  });

  return restoredNodes;
}

function appendHistoryEntry(
  entries: DiagramNodeHistoryEntry[],
  entry: DiagramNodeHistoryEntry
): DiagramNodeHistoryEntry[] {
  return [...entries, entry].slice(-MAX_HISTORY_ENTRIES);
}

export function recordNodeHistory<T extends DiagramNodeHistoryState>(
  previousState: T,
  nextState: T,
  actionType: string,
  mergePreviousDrag = false,
  supplement?: NodeHistorySupplement<T>
): T {
  // A drop and its group change are one undo step. Restore the drag's starting
  // nodes before recording the combined change, but keep the new group position.
  if (
    mergePreviousDrag &&
    previousState.nodeHistory.past.at(-1)?.actionType === 'NODE_DRAG_STOP'
  ) {
    previousState = undoNodeHistory(previousState, supplement);
  }
  if (previousState.nodes === nextState.nodes) return nextState;

  return {
    ...nextState,
    nodeHistory: {
      past: appendHistoryEntry(previousState.nodeHistory.past, {
        ...createHistoryEntry(actionType, previousState.nodes, nextState.nodes),
        supplementalSnapshot: supplement?.createSnapshot(
          previousState,
          nextState
        ),
      }),
      future: [],
      gesture: previousState.nodeHistory.gesture,
    },
  };
}

export function beginNodeHistoryGesture<T extends DiagramNodeHistoryState>(
  state: T,
  kind: DiagramNodeHistoryGesture['kind']
): T {
  if (state.nodeHistory.gesture) return state;

  return {
    ...state,
    nodeHistory: {
      ...state.nodeHistory,
      gesture: {
        kind,
        nodes: createNodeSnapshots(state.nodes),
      },
    },
  };
}

export function completeNodeHistoryGesture<T extends DiagramNodeHistoryState>(
  state: T,
  kind: DiagramNodeHistoryGesture['kind'],
  actionType: string
): T {
  const { gesture } = state.nodeHistory;
  if (gesture?.kind !== kind) return state;

  return {
    ...state,
    nodeHistory: {
      past: appendHistoryEntry(
        state.nodeHistory.past,
        createHistoryEntry(actionType, gesture.nodes, state.nodes)
      ),
      future: [],
      gesture: null,
    },
  };
}

export function undoNodeHistory<T extends DiagramNodeHistoryState>(
  state: T,
  supplement?: NodeHistorySupplement<T>
): T {
  const previous = state.nodeHistory.past[state.nodeHistory.past.length - 1];
  if (!previous) return state;
  const restoredNodes = restoreNodeSnapshot(
    previous.nodes,
    previous.nodeIndexes,
    previous.sourceNodes,
    state.nodes
  );

  const restoredState =
    supplement && previous.supplementalSnapshot !== undefined
      ? supplement.restoreSnapshot(
          { ...state, nodes: restoredNodes },
          previous.supplementalSnapshot
        )
      : { ...state, nodes: restoredNodes };

  return {
    ...restoredState,
    nodeHistory: {
      past: state.nodeHistory.past.slice(0, -1),
      future: appendHistoryEntry(state.nodeHistory.future, {
        ...createHistoryEntry(previous.actionType, state.nodes, restoredNodes),
        supplementalSnapshot: supplement?.createSnapshot(state, restoredState),
      }),
      gesture: null,
    },
  };
}

export function redoNodeHistory<T extends DiagramNodeHistoryState>(
  state: T,
  supplement?: NodeHistorySupplement<T>
): T {
  const next = state.nodeHistory.future[state.nodeHistory.future.length - 1];
  if (!next) return state;
  const restoredNodes = restoreNodeSnapshot(
    next.nodes,
    next.nodeIndexes,
    next.sourceNodes,
    state.nodes
  );

  const restoredState =
    supplement && next.supplementalSnapshot !== undefined
      ? supplement.restoreSnapshot(
          { ...state, nodes: restoredNodes },
          next.supplementalSnapshot
        )
      : { ...state, nodes: restoredNodes };

  return {
    ...restoredState,
    nodeHistory: {
      past: appendHistoryEntry(state.nodeHistory.past, {
        ...createHistoryEntry(next.actionType, state.nodes, restoredNodes),
        supplementalSnapshot: supplement?.createSnapshot(state, restoredState),
      }),
      future: state.nodeHistory.future.slice(0, -1),
      gesture: null,
    },
  };
}

export function resetNodeHistory<T extends DiagramNodeHistoryState>(
  state: T
): T {
  return { ...state, nodeHistory: createEmptyNodeHistory() };
}

export function shouldRecordNodeChanges(
  changes: NodeChange[],
  history: DiagramNodeHistory
): boolean {
  return changes.some((change) => {
    if (change.type === 'add' || change.type === 'remove') return true;
    if (history.gesture) return false;
    if (change.type === 'replace') return true;

    // Alignment and distribution emit one position batch with dragging=false.
    // Drag gestures have their own start snapshot and remain armed until stop.
    return change.type === 'position' && change.dragging === false;
  });
}
